import moment from 'moment';
import { template } from 'lodash';
import { parseFormattedNumber } from 'helpers/Utils';

/*
 * Form validation util
 */
export const isAlphaNumeric = (value) => {
  const alphaNumeric = /^[a-zA-Z0-9]+$/;
  return !!value && alphaNumeric.test(value);
};

export const isSpecificLength = (
  value,
  minLength,
  maxLength
) => {
  if (minLength && !maxLength) {
    return value && value.toString().length >= minLength;
  }
  if (!minLength && maxLength) {
    return value && value.toString().length <= maxLength;
  }
  return value && value.toString().length >= minLength && value.toString().length <= maxLength;
};

export const isAlphaUppercaseNumeric = (value) => {
  const uppercaseLetterAndNumber = /^[A-Z0-9][A-Z0-9]+$/;
  return uppercaseLetterAndNumber.test(value);
};

export const isEnglishCharactersOrSpace = (value) => {
  const letter = /^[A-Za-z\s]+$/;
  return letter.test(value);
};

export const isEnglishCharactersOrSpaceOrSymbol = (value) => {
  // eslint-disable-next-line
  const letter = /^[A-Za-z\s0-9\-\,\/\.]+$/;
  return letter.test(value);
};

export const isChineseCharactersOrEmpty = (value) => {
  if (typeof value === 'undefined') return false;
  if (value.length === 0) return true;
  if (/[a-zA-Z0-9!$#@\-%^&*()_+|~=`{}[\]:";'<>?,.\\/wèéòàùì§«»ω⊙¤°℃℉€¥£¢¡®©]+/.test(value)) return false;
  const letter = /[\u4e00-\u9fff]|[\u3400-\u4dbf]|[\u{20000}-\u{2a6df}]|[\u{2a700}-\u{2b73f}]|[\u{2b740}-\u{2b81f}]|[\u{2b820}-\u{2ceaf}]|[\uf900-\ufaff]|[\u3300-\u33ff]|[\ufe30-\ufe4f]|[\uf900-\ufaff]|[\u{2f800}-\u{2fa1f}]/u;
  return letter.test(value);
};

export const isSelection = (value, options) => {
  let valid = false;
  options.forEach((option) => {
    if (option.value === value) {
      valid = true;
    }
  });
  return valid;
};

export const isFieldMatch = (input1, input2) => ((!input1 || !input2) ? true : input1 === input2);

export const isTextCorrectLength = (text, start, end) => (
  text ? text.length >= start && text.length <= end : false
);

export const isNumberCorrectLength = (text, start, end) => {
  const isNumber = /^[0-9]+$/;
  return isNumber.test(text) && text ? `${text}`.length === start || `${text}`.length === end : false;
};

export const isNumberLengthInRange = (text, start, end) => {
  const isNumber = /^[0-9]+$/;
  return isNumber.test(text) && text ? text.toString().length >= start
  && text.toString().length <= end : false;
};

export const isNumberInDynamicRange = (
  value,
  errMsgObj,
  valueTooLowMessage,
  valueTooHighMessage,
) => {
  if (errMsgObj && value) {
    const { minValue, maxValue } = errMsgObj;
    let errorMessage = null;
    if (minValue > value) {
      errorMessage = template(valueTooLowMessage)({ minValue });
    }
    if (maxValue < value) {
      errorMessage = template(valueTooHighMessage)({ maxValue });
    }
    const isValidValue = (minValue <= value) && (maxValue >= value);
    return {
      isValidValue,
      errorMessage,
    };
  }
  return {};
};

export const isNumberInRange = (number, start, end) => {
  const isNumber = /[0-9]/;
  if (number === '-0') {
    return false;
  }
  const parsedNumber = parseFormattedNumber(number);
  const isExceedStart = (start === null || start === undefined) || parsedNumber >= start;
  const isBelowEnd = (end === null || end === undefined) || parsedNumber <= end;
  return isNumber.test(parsedNumber)
    ? (isExceedStart && isBelowEnd)
    : false;
};

export const isName = (value, forbiddenValues) => {
  // eslint-disable-next-line
  const letter = /^([a-zA-Z]+[’']{0,1}[\s\-]?)+$/;
  let forbiddenValuesArray = [];
  if (forbiddenValues) {
    forbiddenValuesArray = forbiddenValues.filter(i => value.includes(i));
  }
  return letter.test(value) && isTextCorrectLength(value, 1, 50)
    && (forbiddenValuesArray?.length === 0);
};

export const isFullName = (value, forbiddenValues) => {
  // eslint-disable-next-line
  const letter = /^([a-zA-Z]+[’']{0,1}[\s\-]?)+$/;
  let forbiddenValuesArray = [];
  if (forbiddenValues) {
    forbiddenValuesArray = forbiddenValues.filter(i => value.includes(i));
  }
  return value && letter.test(value)
    && (forbiddenValuesArray?.length === 0);
};

export const isMultipleOf = (value, multiple) => (value % multiple === 0);

export const isHKPhoneNumber = (value) => {
  const number = /[0-9]/;
  const hkNumber = /^[23456789]/;
  const notNumber = /^[0-9]*$/g;
  return (
    notNumber.test(value)
    && number.test(value)
    && hkNumber.test(value)
    && isNumberCorrectLength(value.toString(), 8, 11)
  );
};

export const isSGPhoneNumber = (value) => {
  const number = /[0-9]/;
  const sgNumber = /^[89]/;
  const notNumber = /^[0-9]*$/g;
  return (
    notNumber.test(value)
    && number.test(value)
    && sgNumber.test(value)
    && isNumberCorrectLength(value.toString(), 8)
  );
};

export const isAddress = (value) => {
  // eslint-disable-next-line
  const letter = /^[A-Za-z\s0-9#‘’'\-\,\/\.]+$/;
  return value && letter.test(value.trim());
};

export const isPassword = (value) => {
  const atLeastOneUppOneLowerAndOneDigit = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/;
  const caseFail = atLeastOneUppOneLowerAndOneDigit.test(value);
  const lengthFail = isTextCorrectLength(value, 8, 50);
  return caseFail && lengthFail;
};

/* https://computerterminal.blogspot.com/2013/02/hong-kong-id-formula-hkid-number-check.html */
export const isHKId = (value) => {
  const HKIdRegex = /^([A-Za-z]{1,2})([0-9]{6})(\(([Aa0-9])\)|[Aa0-9])$/;
  const match = (value || '').match(HKIdRegex);
  if (!match) return false;
  const [
    // eslint-disable-next-line no-unused-vars
    _,
    letters,
    digits,
    checkDigit1,
    checkDigit2,
  ] = match;
  const checkDigit = checkDigit2 || checkDigit1;
  const values = (letters + digits).split('').map(s => parseInt(s, 36));
  if (letters.length < 2) values.unshift(36);
  values.push(0, 0);
  values.reverse();
  const checksum = values.reduce((acc, cur, idx) => acc + cur * idx);
  const computedCheckDigit = ((11 - (checksum % 11)) % 11).toString(16);
  return checkDigit.toUpperCase() === computedCheckDigit.toUpperCase();
};

export const isSGId = (value) => {
  const SGIdRegex = /^[STFGstfg]\d{7}[A-Za-z]$/;
  const isRegexValid = value && SGIdRegex.test(value);

  if (isRegexValid) {
    const copyValues = [...value];
    const weightTable = [2, 7, 6, 5, 4, 3, 2];
    const alphabetCheck = {
      // the values are arrays to be checked against
      // the checkDigit value for NRIC, starting with corresponding keys:
      S: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'Z', 'J'],
      T: ['H', 'I', 'Z', 'J', 'A', 'B', 'C', 'D', 'E', 'F', 'G'],
      F: ['K', 'L', 'M', 'N', 'P', 'Q', 'R', 'T', 'U', 'W', 'X'],
      G: ['T', 'U', 'W', 'X', 'K', 'L', 'M', 'N', 'P', 'Q', 'R'],
    };
    const numbers = (copyValues)
      .filter(s => Number.isInteger(parseInt(s, 10)))
      .map(t => parseInt(t, 10));
    const checkDigit = 11 - ((numbers.reduce((acc, cur, idx) => (
      acc + cur * weightTable[idx]), 0)) % 11);
    const checkCharacter = alphabetCheck[value[0].toUpperCase()][(checkDigit - 1)];
    const lastItem = value.slice(-1)[0].toUpperCase();
    if (checkCharacter === lastItem) {
      return true;
    }
  }
  return false;
};

export const validateDob = (
  dobMoment,
  invalidDateErrorMessage,
  dateOutOfRangeErrorMessage,
  minAge,
  maxAge,
  dateInFutureErrorMessage = null,
  dateBeyondReasonableYearsAgoErrorMessage = null,
  ageAtMoment = null,
) => {
  let isValidDate = true;
  let errorMessage = null;

  if (!dobMoment.isValid()) {
    isValidDate = false;
    errorMessage = invalidDateErrorMessage;
  } else {
    const years = ageAtMoment ? ageAtMoment.diff(dobMoment, 'years', true) : moment().startOf('day').diff(dobMoment, 'years', true);
    if (years < minAge) {
      isValidDate = false;
      if (years <= 0 && dateInFutureErrorMessage !== null) {
        if (dobMoment.isSame(moment().startOf('day'))) {
          errorMessage = dateOutOfRangeErrorMessage;
        } else {
          errorMessage = dateInFutureErrorMessage;
        }
      } else {
        errorMessage = dateOutOfRangeErrorMessage;
      }
    } else if (years >= maxAge) {
      isValidDate = false;
      if (dateBeyondReasonableYearsAgoErrorMessage !== null) {
        errorMessage = dateBeyondReasonableYearsAgoErrorMessage;
      } else {
        errorMessage = dateOutOfRangeErrorMessage;
      }
    }
  }

  return {
    isValidDate,
    errorMessage,
  };
};

export const validateExpiryDate = (
  dobMoment,
  invalidDateErrorMessage,
  dateOutOfRangeErrorMessage,
  allowInThePast,
  maxYears,
  maxMonths,
) => {
  let isValidDate = true;
  let errorMessage = null;

  if (!dobMoment.isValid()) {
    isValidDate = false;
    errorMessage = invalidDateErrorMessage;
  } else if (!allowInThePast && moment().startOf('day').isAfter(dobMoment)) {
    isValidDate = false;
    errorMessage = dateOutOfRangeErrorMessage;
  } else if (maxYears && moment().add(maxYears, 'year').isBefore(dobMoment)) {
    isValidDate = false;
    errorMessage = dateOutOfRangeErrorMessage;
  } else if (maxMonths && moment().startOf('day').add(maxMonths, 'month').isSameOrBefore(dobMoment)) {
    isValidDate = false;
    errorMessage = dateOutOfRangeErrorMessage;
  }

  return {
    isValidDate,
    errorMessage,
  };
};

export const isInputNumber = (type, value) => {
  if ((type === 'tel' || type === 'number' || type === 'currency') && !/^[0-9]*$/.test(value)) {
    return false;
  }
  return true;
};

export const castToDate = (date) => {
  if (typeof date === 'number' || typeof date === 'string') {
    return new Date(date);
  }
  return date;
};

export const inDateRange = (date, minDate, maxDate) => {
  const theDate = castToDate(date);
  const lower = castToDate(minDate);
  const upper = castToDate(maxDate);
  return (lower <= theDate && theDate <= upper);
};
